import { NoAudioSourceBanner } from '@modules/feedback/components/AudioType/NoAudioSourceBanner';
import {
  getStartEndTimeFromAudioSummaryMetadata,
  isEmptyAudioSourceFeedback,
  isKeywordSummaryAudioBeingPlayed,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { KeywordsAndReasonsSummary } from '@modules/feedback/components/KeywordsAndReasonsSummary';
import useFeedbackListStore, {
  FeedbackListStore,
} from '@modules/feedback/store/useFeedbackListStore';
import { Stack } from '@mui/material';
import { SummaryMetadata } from 'generated/graphql';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { UUID } from 'v2/core/entities/uuid';
import { shallow } from 'zustand/shallow';

const getFeedbackListStoreState = (
  state: FeedbackListStore,
  feedbackID: UUID,
) => {
  return {
    currentPlayingMsgIndex:
      state.feedbacks[feedbackID]?.currentPlayingMsgIndex ?? 0,
    setSummaryTimeToPlay: state.setSummaryTimeToPlay,
    setSelectedSeekJumpTime: state.setSelectedSeekJumpTime,
  };
};

interface AudioKeywordsAndReasonsSummaryProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[] | undefined;
}

export const AudioKeywordsAndReasonsSummary = ({
  feedback,
  selectedLabels,
}: AudioKeywordsAndReasonsSummaryProps) => {
  const {
    currentPlayingMsgIndex,
    setSummaryTimeToPlay,
    setSelectedSeekJumpTime,
  } = useFeedbackListStore(
    (state) => getFeedbackListStoreState(state, feedback.id),
    shallow,
  );

  if (feedback.content.type !== FeedbackContentType.Audio) {
    return null;
  }

  const audioConversation = feedback.content.audioConversationEn;

  const jumpAudioToSummaryStartTime = (
    metadata: SummaryMetadata | undefined,
  ) => {
    const { startTime, endTime } = getStartEndTimeFromAudioSummaryMetadata(
      metadata,
      audioConversation,
    );
    setSelectedSeekJumpTime(startTime, feedback.id);
    setSummaryTimeToPlay({ startTime, endTime }, feedback.id);
    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Summary List: Played Audio',
      feedbackID: feedback.id,
    });
  };

  const isSummaryBeingPlayed = (metadata: SummaryMetadata | undefined) => {
    return isKeywordSummaryAudioBeingPlayed(metadata, currentPlayingMsgIndex);
  };

  const isEmptySourceFeedback = isEmptyAudioSourceFeedback(feedback);

  return (
    <Stack rowGap={2} height="100%">
      {isEmptySourceFeedback && (
        <Stack direction="row" justifyContent="center" width="100%">
          <NoAudioSourceBanner feedback={feedback} />
        </Stack>
      )}
      <KeywordsAndReasonsSummary
        feedback={feedback}
        selectedLabels={selectedLabels}
        onClick={jumpAudioToSummaryStartTime}
        shouldAnimate={isSummaryBeingPlayed}
      />
    </Stack>
  );
};
